/**
 * InventoryPage - Full-page inventory view with large icons and numbers
 * Dedicated tab for students to see all team items clearly
 */

import React from 'react';
import { InventoryItem } from '../models/Inventory';
import { InventoryIcon } from '../utils/inventoryIcons';

interface InventoryPageProps {
  inventory: InventoryItem[];
  teamEnergyPacks: number;
}

export const InventoryPage: React.FC<InventoryPageProps> = ({ inventory, teamEnergyPacks }) => {
  if (inventory.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center rounded-2xl border border-[#CED4DE] bg-white shadow-sm">
        <div className="size-12 rounded-full bg-[#E2E5EC] flex items-center justify-center mb-4">
          <svg className="size-6 text-[#12356B]" fill="currentColor" viewBox="0 0 24 24"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">No items yet</h3>
        <p className="text-sm text-slate-500">Your team inventory is empty.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-[#12356B]">Team Inventory</h2>
          <span className="px-2.5 py-0.5 rounded-full bg-[#12356B]/10 text-[#12356B] text-sm font-semibold">
            {inventory.length} {inventory.length === 1 ? 'item' : 'items'}
          </span>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#DD6832]/10 border border-[#DD6832]/20">
          <span className="text-sm font-medium text-[#DD6832]">Energy Packs</span>
          <span className="text-lg font-bold text-[#DD6832]">{teamEnergyPacks}</span>
        </div>
      </div>

      {/* Inventory Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {inventory.map((item) => (
          <div
            key={item.id}
            className="bg-white rounded-xl border border-[#CED4DE] p-4 flex flex-col items-center text-center gap-2 shadow-sm"
          >
            <InventoryIcon itemId={item.id} className="w-16 h-16" />
            <span className="text-base font-semibold text-slate-900 leading-tight">
              {item.name}
            </span>
            <span className="text-3xl font-bold text-[#367A25]">
              {item.quantity}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InventoryPage;
